import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateProtocolFromDocument, validateDocumentFile, formatFileSize, getDocumentIcon, DocumentUploadRequest } from '../../../utils/documentProtocolUtils';
import { useAppSelector } from '../../../hooks/useAppSelector';
import './DocumentUploadDialog.scss';

interface DocumentUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (jsonProtocol: string) => void;
}

const DocumentUploadDialog: React.FC<DocumentUploadDialogProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const navigate = useNavigate();
  const currentUser = useAppSelector(state => state.user);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [specialty, setSpecialty] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string>('');
  const [dragActive, setDragActive] = useState(false);

  // Сброс состояния при открытии/закрытии диалога
  React.useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setSpecialty(currentUser?.specialty || '');
      setError('');
      setUploadProgress(0);
    }
  }, [isOpen, currentUser]);

  const handleFileSelect = useCallback((file: File) => {
    const validation = validateDocumentFile(file);
    if (!validation.isValid) {
      setError(validation.error || 'Неверный формат файла');
      return;
    }
    
    setSelectedFile(file);
    setError('');
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Выберите файл документа');
      return;
    }

    setIsProcessing(true);
    setUploadProgress(0);
    setError('');

    try {
      // Симуляция прогресса загрузки
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const request: DocumentUploadRequest = {
        file: selectedFile,
        specialty: specialty || undefined
      };

      const response = await generateProtocolFromDocument(request);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success) {
        // Если есть callback для обновления существующей формы
        if (onSuccess) {
          onSuccess(response.data.content); // Заменили json_protocol на content
          onClose();
        } else {
                  // Иначе переходим на страницу создания протокола с данными
        // Сохраняем данные в sessionStorage для избежания проблем с длинными URL
        sessionStorage.setItem('documentProtocolData', response.data.content); // Заменили json_protocol на content
        sessionStorage.setItem('documentConfidence', response.data.confidence.toString());
        navigate(`/protocols/create?fromDocument=true&confidence=${response.data.confidence}`);
        onClose();
        }
      } else {
        setError('Ошибка обработки документа: ' + (response.errors.join(', ') || 'Неизвестная ошибка'));
      }
    } catch (err: any) {
      setError('Ошибка загрузки: ' + (err.message || 'Неизвестная ошибка'));
    } finally {
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  if (!isOpen) return null;
  
  // Если пользователь не загружен, не отображаем диалог
  if (!currentUser) return null;

  return (
    <div className="pdf-upload-overlay" onClick={handleClose}>
      <div className="pdf-upload-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Создать протокол из документа</h2>
          <button 
            className="close-button" 
            onClick={handleClose}
            disabled={isProcessing}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2"/>
              <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>
        </div>

        <div className="dialog-content">
          <div className="upload-section">
            <div className="specialty-field">
              <label>Специальность (опционально)</label>
              <input
                type="text"
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                placeholder="Например: терапия, кардиология"
                disabled={isProcessing}
              />
            </div>

            <div 
              className={`file-drop-zone ${dragActive ? 'drag-active' : ''} ${selectedFile ? 'has-file' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {selectedFile ? (
                <div className="selected-file">
                  <div className="file-icon">{getDocumentIcon(selectedFile)}</div>
                  <div className="file-info">
                    <div className="file-name">{selectedFile.name}</div>
                    <div className="file-size">{formatFileSize(selectedFile.size)}</div>
                  </div>
                  <button 
                    className="remove-file"
                    onClick={() => setSelectedFile(null)}
                    disabled={isProcessing}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="drop-zone-content">
                  <div className="upload-icon">📄</div>
                  <h3>Перетащите документ сюда</h3>
                  <p>или</p>
                  <label className="file-select-button">
                    Выберите файл
                    <input
                      type="file"
                      accept=".pdf,.docx,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={handleFileChange}
                      disabled={isProcessing}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <p className="file-requirements">
                    Поддерживаются файлы: PDF, Word (.docx), Excel (.xlsx) до 20MB
                  </p>
                </div>
              )}
            </div>

            {isProcessing && (
              <div className="processing-indicator">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p>Обрабатываем документ... {uploadProgress}%</p>
              </div>
            )}

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="dialog-footer">
          <button 
            className="button-secondary"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Отмена
          </button>
          <button 
            className="button-primary"
            onClick={handleUpload}
            disabled={!selectedFile || isProcessing}
          >
            {isProcessing ? 'Обработка...' : 'Создать протокол'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadDialog;
